import Papa from 'papaparse'
import moment from 'moment'

/**
 * Parses a CSV-like list of coordinates to an array and checks for errors.
 * See unit test to learn about allowed formats.
 *
 * @param {string} csv - A delimiter-separated list of coordinates and
 *          optionally time intervals.
 * @param {string} delimiter - The CSV delimiter
 * @param {string} hasTimeIntervals - Interpret line value 5 and 6 as dates
 * @returns {object} Contains arrays with parsed coordinats, timer intervals,
 *          result of validity check and line numbers
 */
const parseValidateCsv = (coordinatesList, delimiter='', hasTimeIntervals) => {

    if (![',', ' ', ';', '\t', ''].includes(delimiter)) {
        throw Error('Invalid CSV delimiter specified')
    }

    let result = {
        lines: [],
        numberOfLines: 0,
        lineNumbers: [],
        lineStatus: [],  // e.g. "OK" or "Altitude implausible."
        linesWithErrors: [],
        headerDetected: false,
    }


    /*
        Parse CSV
    */

    const csvParserConfig = { delimiter }
    const parsed = Papa.parse(coordinatesList, csvParserConfig)
    result.lines = parsed.data


    /*
        Detect header and reorder
    */

    let hasHeader = false
    let headerKeywordsOrder = []
    let numberOfDetectedHeaderKeywords = 0
    let firstRow = null

    const headerKeywords = {
        lat: ['lat', 'latitude'],
        lon: ['lon', 'lng', 'long', 'longitude'],
        asl: ['asl', 'altitude', 'height', 'meters above sea level'],
        ignore: ['ignore', 'omit']
    }

    if(hasTimeIntervals) {
        headerKeywords.start = 
            ['start', 'start date', 'startdate', 'begin', 'beginning', 
            'planting date', 'planting date female', 'planting date male'],
        headerKeywords.end = 
            ['end', 'ending', 'end date', 'enddate', 'stop', 'harvest date', 
            'harvest date male', 'harvest date female']
    }

    if(typeof result.lines[0] !== 'undefined') {
        firstRow = result.lines[0]
            .slice()
            .map(v => v.toLowerCase().trim())
    }

    // is the first row a header?
    for (let key in headerKeywords) {

        // @todo: move null check outside loop
        if(firstRow === null || hasHeader) {
            break
        }

        let synonyms = headerKeywords[key]
        
        // is the first value a header keyword? 
        if (headerKeywords.hasOwnProperty(key)
            && synonyms.includes(firstRow[0]))
        {
            hasHeader = true
        }

        /* otherwise, if the first row contains at least 2 header keywords,
            assume it's a header */
        else if (headerKeywords.hasOwnProperty(key)
            && synonyms.filter(kw => firstRow.includes(kw)).length > 0)
        {
            numberOfDetectedHeaderKeywords++
            if(numberOfDetectedHeaderKeywords === 2) {
                hasHeader = true
            }
        }

        if(hasHeader) {
            result.lineStatus[0] = 'OK, header detected'
            result.headerDetected = hasHeader
            break
        }
    }
    
    if (hasHeader) {

        // determine the header order
        firstRow.forEach((value, i) => {
            for (let key in headerKeywords) {
                if (headerKeywords.hasOwnProperty(key)
                    && headerKeywords[key].includes(value.trim()))
                {
                    headerKeywordsOrder.push(key)
                    return
                }
            }
            headerKeywordsOrder.push('name' + i)
        })

        // remove header, no longer needed
        result.lines.shift()

        // reorder the parsed CSV
        result.lines = result.lines.map((line, i) => {

            let reordered = []

            reordered[0] = line[headerKeywordsOrder.indexOf('lat')]
            reordered[1] = line[headerKeywordsOrder.indexOf('lon')]

            if(headerKeywordsOrder.includes('asl')) {
                reordered[2] = line[headerKeywordsOrder.indexOf('asl')]
            } else {
                reordered[2] = ''
            }

            reordered[3] = ''
            for(let i = 0; i <= headerKeywordsOrder.length; i++) {
                let namePart = line[headerKeywordsOrder.indexOf('name' + i)]
                if(typeof namePart !== 'undefined') {
                    reordered[3] += line[headerKeywordsOrder.indexOf('name' + i)] + ' '
                }
            }
            reordered[3] = reordered[3].trim()

            if(hasTimeIntervals) {
                reordered[4] = line[headerKeywordsOrder.indexOf('start')]
                reordered[5] = line[headerKeywordsOrder.indexOf('end')]
            }

            return reordered
        })

    }


    /*
        Remove surrounding spaces and quotes 
        Note: The CSV parser also already removed some quotes according to
        RFC4180, so this step is not compliant to RFC, as well as the space
        trimming during JS type coercion later on. However, it is more
        intuitive to the user.
    */

    result.lines = result.lines.map(line => 
        line.map(v => {
            if(typeof v === 'undefined') {
                return v
            }
            v = v.trim()
            if(v[0] === '"') v = v.substring(1)
            if(v[v.length - 1] === '"') v = v.substring(0,v.length - 1)
            return v
        })
    )


    /*
        Check for validity
    */

    result.lines.forEach((line, lineNumber) => {

        if(hasHeader) {
            lineNumber++
        }

        const setLineError = message => {
            result.lineStatus[lineNumber] = message
            result.linesWithErrors.push(lineNumber + 1)
        }

        result.lineStatus[lineNumber] = 'OK'

        // is line empty?
        if (line.length === 1 && line[0] === '') {
            setLineError('Line is empty')
            return
        }

        // Latitude
        line[0] = Number(line[0])
        if (Math.abs(line[0]) > 90) {
            setLineError('Latitude out of bounds')
            return
        }
        if (isNaN(line[0])) {
            setLineError('Invalid latitude format')
            return
        }

        // Longitude
        if (line[1] === '' || line[1] === undefined) {
            setLineError('Longitude is missing')
            return
        } else {
            line[1] = Number(line[1])
            if (Math.abs(line[1]) > 180) {
                setLineError('Longitude out of bounds')
                return
            }
            if (isNaN(line[1])) {
                setLineError('Invalid longitude format')
                return
            }
        }

        // Altitude
        if (line[2] === '') {
            line[2] = null
        } else if (line[2] !== undefined) {
            line[2] = Number(line[2])
            if (line[2] === -999) {
                line[2] = null
            }
            if (line[2] < -100 || line[2] > 20000) {
                setLineError('Altitude out of bounds')
                return
            }
            if (isNaN(line[2])) {
                setLineError('Invalid altitude format')
                return
            }
        }

        // Location name
        if (line[3] !== undefined) {
            if (line[3].length > 100) {
                setLineError('Location name exceeds 100 characters')
                return
            }
        }

        if (!hasTimeIntervals && line.length > 4) {
            setLineError('Too many values')
            return
        } else if (!hasTimeIntervals) {
            return
        }

        // Dates

        if (typeof line[4] === 'undefined' || line[4] === '') {
            setLineError('Missing start date')
            return
        }

        if (typeof line[5] === 'undefined' || line[5] === '') {
            setLineError('Missing end date')
            return
        }

        let startDateHasHours = line[4].indexOf('T') !== -1
        let endDateHasHours = line[5].indexOf('T') !== -1

        const startDate = moment(line[4].trim(), startDateHasHours ? "YYYY-MM-DDTHH" : "YYYY-MM-DD", true);
        const endDate = moment(line[5].trim(), endDateHasHours ? "YYYY-MM-DDTHH" : "YYYY-MM-DD", true);

        if (!startDate.isValid()) {
            setLineError('Invalid start date format')
            return
        }

        if (!endDate.isValid()) {
            setLineError('Invalid end date format')
            return
        }

        if (startDate > endDate) {
            setLineError('Start date after end date')
            return
        }

        const duration = moment.duration(endDate.diff(startDate))

        if(startDateHasHours || endDateHasHours) {
            const days = Math.floor(duration.asDays())
            const hours = duration.asHours() - days * 24
            result.lineStatus[lineNumber] =
                `OK, ${days} ${days === 1 ? 'day' : 'days'} ` +
                `and ${hours} ${hours === 1 ? 'hour' : 'hours'}`
        } else {
            const durationDays = Math.ceil(duration.add(1, 'day').asDays())
            result.lineStatus[lineNumber] = `OK, ${durationDays} ${durationDays === 1 ? 'day' : 'days'}`
        }

    })

    // add meta infos

    let numberOfLines = result.lines.length
    if(hasHeader || result.lines.length === 0) numberOfLines++

    result.lineNumbers = [...Array(numberOfLines).keys()].map(v => v + 1)
    result.numberOfLines = numberOfLines
    result.delimiter = parsed.meta.delimiter
        .replace('	', 'Tab')
        .replace(' ', 'Space')


    return result
}

export default parseValidateCsv